import { AxiosRequestConfig } from "axios";
import { Report, ITableReport } from "src/Interfaces/TableInterfaces";
import { OrderBook } from "src/Interfaces/orderInterfaces";
import { IOpenOrder } from "src/Interfaces/marketmaking";
import { Graph_Dto } from "src/Interfaces/orderbookGraphInterfaces";
import { DropdownItem } from "src/Interfaces/dropdownInterfaces";
import { deleteRequest, getRequest, getTable, postDownloadExcel, postRequest, putRequest } from "./requestTemplates";

export interface BaseExecutionBaseSetting {
  baseInstrumentId: string;
  baseSymbol: string;
  firstStepTradeVolume: number | null;
  secondStepTradeVolume: number | null;
}

export interface BaseExecutionSettings {
  referenceValidityMinutes: number | null;
  maxFairPriceChangePercent: number | null;
  minimumBuyingPower: number | null;
  removeExtraOrders: boolean;
  decreaseFirstStepPercent: number | null;
  decreaseSecondStepPercent: number | null;
  increaseFirstStepPercent: number | null;
  increaseSecondStepPercent: number | null;
  baseSettings: BaseExecutionBaseSetting[];
}

export interface BaseExecutionChoice {
  instrumentId: string;
  symbol: string;
  baseInstrumentId: string;
  baseSymbol: string;
}

export interface BaseExecutionAccountChoice { id: number; name: string }

export interface BaseExecutionTaskInput {
  instrumentId: string;
  sahraUserId: number;
  minimumTriggerIntervalSeconds: number;
  referenceTolerancePercent: number | null;
  priceReference: "ClosingPrice" | "Midpoint" | "LastTrade";
  initialFactorMode: "Manual" | "Auto" | "Continues";
  initialFactor: number | null;
  orderType: "Open" | "Offset";
}

export interface BaseExecutionTaskRow extends ITableReport, BaseExecutionTaskInput {
  currentFactor: number | null;
  previousTradingFactor: number | null;
  autoRefill: { value: boolean; putUrlCmd: string };
  symbol: string;
  instrumentName: string;
  tseLink: string;
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
  baseInstrumentId: string;
  baseSymbol: string;
  accountName: string;
  isActive: boolean;
}

export interface BaseExecutionTaskSearch {
  baseInstrumentId?: string;
  instrumentId?: string;
  isActive?: boolean;
}

const executionUrl = "/v1/baseMarketMaking/execution";
export const getBaseExecutionSettings = () =>
  getRequest<BaseExecutionSettings>(`${executionUrl}/settings`);
export const saveBaseExecutionSettings = (input: BaseExecutionSettings) =>
  putRequest<BaseExecutionSettings, BaseExecutionSettings>(`${executionUrl}/settings`, input);
export const getBaseExecutionChoices = () =>
  getRequest<BaseExecutionChoice[]>(`${executionUrl}/instrumentChoices`);
export const getBaseExecutionAccounts = () =>
  getRequest<BaseExecutionAccountChoice[]>(`${executionUrl}/accountChoices`);
export const getBaseExecutionTasks = (
  search: BaseExecutionTaskSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<BaseExecutionTaskRow>> =>
  getTable<BaseExecutionTaskSearch, BaseExecutionTaskRow>(
    `${executionUrl}/tasks/table`, search, offset, pageSize, sortingType, additional, options,
  );
export const getBaseExecutionNetVolumeChart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/netVolumeChart`);
export const getBaseExecutionNetVolume2Chart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/netVolume2Chart`);
export const getBaseExecutionCurrentFactorChart = (taskId: number): Promise<Graph_Dto[]> =>
  getRequest(`${executionUrl}/tasks/${taskId}/currentFactorChart`);
export const addBaseExecutionTask = (input: BaseExecutionTaskInput) =>
  postRequest<BaseExecutionTaskInput, number>(`${executionUrl}/tasks`, input);
export const editBaseExecutionTask = (id: number, input: BaseExecutionTaskInput) =>
  putRequest<BaseExecutionTaskInput, void>(`${executionUrl}/tasks/${id}`, input);
export type BaseExecutionTaskOperation = "play" | "pause" | "resend" | "delete";
export interface BaseExecutionTaskOperationResult {
  changed: number;
  notAllowed: number;
  unready: number;
  dispatchFailed: number;
  fairPriceChangeRejected: number;
  confirmationRequired: boolean;
  remainingTriggerIntervalSeconds: number;
}
export interface BaseExecutionOfferPreviewRow {
  rowNo: number;
  from: number;
  to: number;
  calculatedVolume: number;
  finalVolume: number | null;
  calculatedPrice: number | null;
  flexibilityAmount: number;
  flexibilityPercent: number;
  disabledByBuyingPower: boolean;
  disabledByPriceLimit: boolean;
}
export interface BaseExecutionOfferPreview {
  taskId: number;
  snapshotId: number | null;
  programId: number | null;
  factorSnapshotId: number | null;
  instrumentId: string;
  account: string;
  orderType: string;
  referencePrice: number | null;
  factor: number | null;
  fairPrice: number | null;
  lastSuccessfulFairPrice: number | null;
  fairPriceChangePercent: number | null;
  maxFairPriceChangePercent: number | null;
  fairPriceChangeExceeded: boolean;
  priceLimitsBypassed?: boolean;
  minPricePermit: number | null;
  maxPricePermit: number | null;
  snapshotAt: string | null;
  minimumBuyingPower: number | null;
  isBuyingPowerLimited: boolean;
  buy: BaseExecutionOfferPreviewRow[];
  sell: BaseExecutionOfferPreviewRow[];
}
export const getBaseExecutionPlayPreviews = (ids: number[]) =>
  postRequest<{ ids: number[] }, BaseExecutionOfferPreview[]>(
    `${executionUrl}/tasks/preview`, { ids });
export interface BaseExecutionPreviewVersion {
  snapshotId: number;
  programId: number;
  factorSnapshotId: number | null;
}
export const changeBaseExecutionTask = (id: number, action: BaseExecutionTaskOperation,
  preview?: BaseExecutionPreviewVersion, overrideMinimumTriggerInterval = false) =>
  postRequest<BaseExecutionPreviewVersion & { overrideMinimumTriggerInterval: boolean }, BaseExecutionTaskOperationResult>(
    `${executionUrl}/tasks/${id}/${action}`,
    { snapshotId: preview?.snapshotId ?? 0, programId: preview?.programId ?? 0,
      factorSnapshotId: preview?.factorSnapshotId ?? null, overrideMinimumTriggerInterval });
export const changeBaseExecutionTasks = (ids: number[], action: BaseExecutionTaskOperation,
  previews?: Record<number, BaseExecutionPreviewVersion>, overrideMinimumTriggerInterval = false) =>
  postRequest<{ ids: number[]; previews?: Record<number, BaseExecutionPreviewVersion>;
    overrideMinimumTriggerInterval: boolean }, BaseExecutionTaskOperationResult>(
    `${executionUrl}/tasks/bulk/${action}`, { ids, previews, overrideMinimumTriggerInterval });
export interface BaseExecutionOrderBookResponse {
  fullOrderBook: OrderBook | null;
  instrumentStatistic: {
    minPricePermit: number;
    maxPricePermit: number;
    priceYesterday: number | null;
    priceMin: number | null;
    priceMax: number | null;
    final: { price: number } | null;
    trade: { price: number } | null;
  } | null;
  liveDetails: BaseExecutionLiveDetails | null;
  openOrders: IOpenOrder[];
}
export const getBaseExecutionOrderBook = (taskId: string, signal?: AbortSignal) =>
  getRequest<BaseExecutionOrderBookResponse | OrderBook | null>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/orderBook`, { signal });

export interface BaseExecutionMarketDataResponse {
  instrumentStatistic: BaseExecutionOrderBookResponse["instrumentStatistic"];
  liveDetails: BaseExecutionLiveDetails | null;
}
export const getBaseExecutionMarketData = (taskId: string, signal?: AbortSignal) =>
  getRequest<BaseExecutionMarketDataResponse>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/marketData`, { signal });


export const getBaseExecutionOpenOrders = (taskId: string, signal?: AbortSignal) =>
  getRequest<IOpenOrder[]>(`${executionUrl}/tasks/${encodeURIComponent(taskId)}/openOrders`, { signal });

export interface BaseExecutionLiveDetails {
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
  priceLimitsBypassed?: boolean;
  baseInsPriceLive: number | null;
  baseInsPriceYesterday?: number | null;
  liveFairPrice: number | null;
  sentFairPrice?: number | null;
  closingPrice: number | null;
  midpoint: number | null;
  lastFactorTriggerAt: string | null;
  lastTradingPrice: number | null;
}
export const getBaseExecutionLiveDetails = (taskId: string, signal?: AbortSignal) =>
  getRequest<BaseExecutionLiveDetails>(
    `${executionUrl}/tasks/${encodeURIComponent(taskId)}/liveDetails`, { signal });

export interface BaseExecutionProgramRow {
  rowNo: number;
  from: number;
  to: number;
  flexibilityAmount: number;
  flexibilityPercent: number;
}
export interface BaseExecutionTrigger {
  id: number;
  triggerType: string;
  status: string;
  referencePrice: number | null;
  fairPrice: number | null;
  factor: number | null;
  createdAt: string;
}
export const getBaseExecutionTriggers = (taskId: number) =>
  getRequest<BaseExecutionTrigger[]>(`${executionUrl}/tasks/${taskId}/triggers`);
export interface BaseExecutionTriggerRow extends ITableReport {
  createdAt: string;
  triggerType: string;
  isSuccess: boolean;
  status: string;
  changeBasePricePercent: number | null;
  closingPrice: number | null;
  midpoint: number | null;
  lastStockPrice: number | null;
  priceReference: string | null;
  effectivePriceReference: string | null;
  priceReferenceDisplay: string | null;
  referencePrice: number | null;
  factor: number | null;
  fairPrice: number | null;
  orderCount: number;
}
export const getBaseExecutionTriggerTable = (
  search: BaseExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string,
): Promise<Report<BaseExecutionTriggerRow>> =>
  getTable<BaseExecutionDetailSearch, BaseExecutionTriggerRow>(
    `${executionUrl}/tasks/${search.taskId}/triggers/table`, search, offset, pageSize, sortingType);
export interface BaseExecutionProgram {
  buy: BaseExecutionProgramRow[];
  sell: BaseExecutionProgramRow[];
}
export interface BaseExecutionPredefinedProgram extends BaseExecutionProgram {
  id: number;
  name: string;
}
export interface BaseExecutionDetailSearch { taskId?: number }
export interface BaseExecutionOrderRow extends ITableReport {
  instrumentId: string;
  accountName: string;
  side: string;
  programRowNumber: number;
  orderPrice: number;
  executedPrice: number | null;
  createdAt: string;
  updatedAt: string;
  filledVolume: number;
  totalVolume: number;
  pendingVolume: number;
  isActive: boolean;
  message: string | null;
}
export interface BaseExecutionTradeRow extends ITableReport {
  instrumentId: string;
  accountName: string;
  side: string;
  price: number;
  volume: number;
  executedAt: string;
}
export interface BaseExecutionErrorRow extends ITableReport {
  instrumentId: string;
  accountName: string;
  side: string;
  volume: number;
  price: number;
  message: string;
  code: string | null;
  createdAt: string;
}

export const getBaseExecutionProgram = (taskId: number) =>
  getRequest<BaseExecutionProgram>(`${executionUrl}/tasks/${taskId}/program`);
export const saveBaseExecutionProgram = (taskId: number, input: BaseExecutionProgram) =>
  putRequest<BaseExecutionProgram, BaseExecutionProgram>(`${executionUrl}/tasks/${taskId}/program`, input);
export const getBaseExecutionPredefinedPrograms = () =>
  getRequest<BaseExecutionPredefinedProgram[]>(`${executionUrl}/predefinedPrograms`);
export const saveBaseExecutionPredefinedProgram = (input: Omit<BaseExecutionPredefinedProgram, "id">) =>
  postRequest<Omit<BaseExecutionPredefinedProgram, "id">, BaseExecutionPredefinedProgram>(
    `${executionUrl}/predefinedPrograms`, input);
export const deleteBaseExecutionPredefinedProgram = (id: number) =>
  deleteRequest(`${executionUrl}/predefinedPrograms/${id}`);

export const getBaseExecutionOrders = (
  search: BaseExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<BaseExecutionOrderRow>> =>
  getTable<BaseExecutionDetailSearch, BaseExecutionOrderRow>(
    search.taskId == null ? `${executionUrl}/orders/table` : `${executionUrl}/tasks/${search.taskId}/orders/table`,
    search, offset, pageSize, sortingType, additional, options);
export const cancelBaseExecutionOrder = (orderId: number) =>
  postRequest<{}, unknown>(`${executionUrl}/orders/${orderId}/cancel`, {});
export const getBaseExecutionTrades = (
  search: BaseExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<BaseExecutionTradeRow>> =>
  getTable<BaseExecutionDetailSearch, BaseExecutionTradeRow>(
    search.taskId == null ? `${executionUrl}/trades/table` : `${executionUrl}/tasks/${search.taskId}/trades/table`,
    search, offset, pageSize, sortingType, additional, options);
export const getBaseExecutionErrors = (
  search: BaseExecutionDetailSearch, offset: number | null, pageSize: number | null,
  sortingType?: string, additional?: DropdownItem, options?: AxiosRequestConfig,
): Promise<Report<BaseExecutionErrorRow>> =>
  getTable<BaseExecutionDetailSearch, BaseExecutionErrorRow>(
    search.taskId == null ? `${executionUrl}/errors/table` : `${executionUrl}/tasks/${search.taskId}/errors/table`,
    search, offset, pageSize, sortingType, additional, options);
